import type { Person } from '@hcengineering/contact'
import type {
  AccountUuid,
  AttachedDoc,
  Blob,
  Class,
  Doc,
  IntegrationKind,
  Mixin,
  Ref,
  Timestamp
} from '@hcengineering/core'
import type { Asset, IntlString, Metadata, Plugin } from '@hcengineering/platform'
import { plugin } from '@hcengineering/platform'
import type { Vacancy } from '@hcengineering/recruit'
import type { IntegrationType } from '@hcengineering/setting'
import type { AnyComponent } from '@hcengineering/ui'

/**
 * @public
 */
export const candidateId = 'dztek-candidate' as Plugin

/**
 * @public
 */
export const candidateIntegrationKind = 'dztek-candidate' as IntegrationKind

/**
 * One employment entry of a parsed resume. Dates are `YYYY-MM` or `YYYY`; an open end is the empty string.
 * @public
 */
export interface Employment {
  company: string
  title: string
  from: string
  to: string
}

/**
 * One education entry of a parsed resume.
 * @public
 */
export interface Education {
  institution: string
  degree: string
  field: string
  from: string
  to: string
}

/**
 * Parsed resume fields, held on a Person as a mixin.
 * @public
 */
export interface ResumeProfile extends Person {
  headline: string
  summary: string
  yearsExperience: number
  skills: string[]
  employers: Employment[]
  education: Education[]
  /** Blob of the resume the profile was parsed from. */
  sourceResumeRef: Ref<Blob> | null
  /** Blob holding the achievement-level parse result in dynamic-cv shape. */
  parsedRef: Ref<Blob> | null
  parsedAt: Timestamp
  parserVersion: string
  tailoredCvs?: number
}

/**
 * @public
 */
export type TailoredCvStatus = 'generated' | 'failed'

/**
 * A CV tailored to one vacancy, attached to the Person it describes.
 * @public
 */
export interface TailoredCv extends AttachedDoc {
  attachedTo: Ref<Person>
  vacancyRef: Ref<Vacancy>
  fileRef: Ref<Blob> | null
  fileName: string
  fileType: string
  fileSize: number
  generatedAt: Timestamp
  /** Account of the user who requested the CV. */
  generatedBy: AccountUuid
  status: TailoredCvStatus
  error?: string
}

export default plugin(candidateId, {
  app: {
    Candidate: '' as Ref<Doc>
  },
  class: {
    TailoredCv: '' as Ref<Class<TailoredCv>>
  },
  mixin: {
    ResumeProfile: '' as Ref<Mixin<ResumeProfile>>
  },
  component: {
    CandidateApp: '' as AnyComponent,
    ImportResume: '' as AnyComponent,
    GenerateCv: '' as AnyComponent,
    ResumeSection: '' as AnyComponent,
    TailoredCvPresenter: '' as AnyComponent,
    Configure: '' as AnyComponent,
    IconCandidate: '' as AnyComponent
  },
  integrationType: {
    ResumeTools: '' as Ref<IntegrationType>
  },
  metadata: {
    ServiceUrl: '' as Metadata<string>
  },
  string: {
    Candidate: '' as IntlString,
    Description: '' as IntlString,
    ResumeProfile: '' as IntlString,
    ResumeProfiles: '' as IntlString,
    TailoredCv: '' as IntlString,
    TailoredCvs: '' as IntlString,
    ResumeTools: '' as IntlString,
    ResumeToolsDescription: '' as IntlString,
    ImportResume: '' as IntlString,
    GenerateTailoredCv: '' as IntlString
  },
  icon: {
    Candidate: '' as Asset,
    Resume: '' as Asset,
    TailoredCv: '' as Asset
  }
})
